// shared/money-input.js — what an amount somebody typed, pasted or imported
// says, in pence: the one rule for reading money written as text.
//
// Every money field on the web, the iPhone (through the facade's
// `readMoneyInput`), the client's pay page and the importers reads through
// `readMoneyText`. It exists because each used to have its own, and most were
// `parseFloat`, which stops at the first character it doesn't know: "1,200.00"
// typed into Record payment recorded £1.00, "1.200,00" £1.20 and "1e3"
// £1,000.00, each without a word.
//
// Accepted: a currency sign or code before or after ("£1,200", "GBP 12",
// "12.50 GBP"), thousands grouped in their places by commas or by spaces
// ("1 200.00", with the no-break and narrow no-break spaces some phones group
// with), a decimal point with or without a digit before it (".50"), a minus
// or brackets for a negative. Refused, never guessed: separators anywhere
// else ("1,2,0,0", "12,00"), the other convention ("1.200,00"), a third
// decimal place, exponents, hex and anything else that isn't an amount. A
// lone decimal point with three digits after it ("1.200") reads two ways,
// £1.20 or £1,200.00 to somebody who groups with a point, so a field asks
// which, showing both (`ambiguousAmountSentence`). A file's cell is read
// with `imported`, where that is a decimal point and places past two are
// fine when they are noughts ("12.5000", which Xero writes). Whether a range
// of amounts makes sense (more than nought, no more than the balance) is
// each field's own question, asked of the pence this returns.
//
// The web, the server and the pay page are en-GB: a comma groups thousands
// and a point is the decimal. A phone set to a locale that writes "12,50"
// has a keypad that types a comma, so the iPhone says `decimalComma` there,
// and the two swap roles, except that a point with no comma is still a
// decimal point: the app fills its own fields in as "450.55". So there a
// lone comma or a lone point with three digits after it is asked about.
// Nothing else says `decimalComma`.
//
// A quantity is read by the same rule (`readQuantityInput`), with no
// currency and no sign: "1,000" m² is a thousand, never the 1 parseFloat
// found at its front.
//
// Pure: no imports, no DOM, no node builtins. `readMoneyText` and
// `ambiguousAmountSentence` refer to nothing outside themselves, because the
// pay page (server/routes/stripe.js) embeds their source in the client's page.

/** What a field says when what was typed isn't an amount. */
export const MONEY_INPUT_COPY = {
  unreadable: "Enter an amount, like 1,200.00.",
  decimals: "Enter an amount to no more than 2 decimal places."
};

/**
 * What a quantity field says when what was typed isn't a quantity. A line's
 * quantity is kept to 2 decimal places (ADR-034), as a price is.
 */
export const QUANTITY_INPUT_COPY = {
  unreadable: "Enter a quantity, like 1,000 or 2.5.",
  decimals: "Enter a quantity to no more than 2 decimal places."
};

/**
 * Money written as text, in pence.
 *
 * `imported` is a file's cell rather than a person's typing: a lone point
 * with three digits after it is then a decimal point (a file is written by
 * software, which never groups "1.200"), and decimal places past two are
 * allowed when they are noughts ("12.5000", which Xero writes). Typed, the
 * first is asked about (`ambiguous`, with both `readings`) and the second
 * refused (`decimals`).
 *
 * @param {unknown} raw
 * @param {{ decimalComma?: boolean, imported?: boolean }} [options]
 * @returns {{ pence: number|null, error?: "unreadable"|"decimals"|"ambiguous", readings?: { grouped: number, decimal: number } }} null pence and no error for a blank
 */
export function readMoneyText(raw, options) {
  const decimalComma = Boolean(options && options.decimalComma);
  const imported = Boolean(options && options.imported);
  // Every space is one space here, the no-break space (U+00A0) and the
  // narrow no-break one (U+202F) some phones group thousands with included.
  let text = String(raw == null ? "" : raw).replace(/\s+/g, " ").trim().replace(/^'/, "");
  if (!text) return { pence: null };
  const unreadable = { pence: null, error: "unreadable" };
  const currency = /^(?:gbp|usd|eur|aud|cad|nzd|chf|sek|nok|dkk|kr|fr\.?|[a-z]{0,2}\$|£|€)\s*|\s*(?:gbp|usd|eur|aud|cad|nzd|chf|sek|nok|dkk|kr|fr\.?|[a-z]{0,2}\$|£|€)$/gi;
  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1).trim();
  }
  text = text.replace(currency, "").trim();
  if (/^[-−]/.test(text)) {
    negative = !negative;
    text = text.slice(1).trim();
  }
  text = text.replace(currency, "").trim();
  // The decimal mark and what groups thousands, as this field writes them.
  const mark = decimalComma ? "," : ".";
  let whole = "";
  let decimals = "";
  // Whether the decimal mark is the only separator, which is the one shape
  // that can read two ways: "1.200" is £1.20, or £1,200.00 to somebody who
  // groups thousands with a point.
  let lone = false;
  let match = /^(\d{1,3}(?: \d{3})+)(?:([.,])(\d*))?$/.exec(text);
  if (match) {
    // A space groups thousands in either convention ("1 200", "1 200.00").
    if (match[2] && match[2] !== mark) return unreadable;
    whole = match[1].split(" ").join("");
    decimals = match[3] || "";
  } else if ((match = decimalComma
    ? /^(\d{1,3}(?:\.\d{3})+)(?:,(\d*))?$/.exec(text)
    : /^(\d{1,3}(?:,\d{3})+)(?:\.(\d*))?$/.exec(text)) && (!decimalComma || match[2] != null || match[1].split(".").length > 2)) {
    // Thousands grouped in their places. A single point group with nothing
    // after it ("1.200") is not here on a decimal-comma phone: the app fills
    // its own fields in as "450.55", so a point is a decimal point there too.
    whole = match[1].split(decimalComma ? "." : ",").join("");
    decimals = match[2] || "";
  } else if ((match = /^(\d*)(?:([.,])(\d*))?$/.exec(text)) && (match[1] || match[3])) {
    // No grouping. On the web a comma here is out of its place ("12,00");
    // on a decimal-comma phone either mark is the decimal point.
    if (match[2] && !decimalComma && match[2] !== ".") return unreadable;
    whole = match[1];
    decimals = match[3] || "";
    lone = Boolean(match[2]);
  } else return unreadable;
  whole = whole.replace(/^0+(?=\d)/, "") || "0";
  if (whole.length > 12) return unreadable;
  if (!imported && lone && decimals.length === 3 && /^[1-9]\d{0,2}$/.test(whole) && decimals[2] === "0") {
    const sign = negative ? -1 : 1;
    return {
      pence: null,
      error: "ambiguous",
      readings: { grouped: sign * Number(whole + decimals) * 100, decimal: sign * (Number(whole) * 100 + Number(decimals.slice(0, 2))) }
    };
  }
  if (decimals.length > 2 && (!imported || /[1-9]/.test(decimals.slice(2)))) return { pence: null, error: "decimals" };
  const pence = Number(whole) * 100 + Number((decimals + "00").slice(0, 2));
  return { pence: negative && pence !== 0 ? -pence : pence };
}

/**
 * The sentence for an amount that reads two ways ("1.200" on the web, "1,200"
 * on a decimal-comma phone), with both readings written as the field writes
 * them: "Enter 1,200.00 or 1.20, whichever you mean." A quantity's readings
 * drop the noughts: "Enter 1,200 or 1.2, whichever you mean."
 *
 * Refers to nothing outside itself, as `readMoneyText` doesn't: the pay page
 * embeds both.
 *
 * @param {{ grouped: number, decimal: number }} readings in hundredths
 * @param {{ decimalComma?: boolean, quantity?: boolean }} [options]
 * @returns {string}
 */
export function ambiguousAmountSentence(readings, options) {
  const decimalComma = Boolean(options && options.decimalComma);
  const quantity = Boolean(options && options.quantity);
  const written = (hundredths) => {
    const size = Math.abs(hundredths);
    const whole = String(Math.floor(size / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, decimalComma ? "." : ",");
    let decimals = String(size % 100).padStart(2, "0");
    if (quantity) decimals = decimals.replace(/0+$/, "");
    return (hundredths < 0 ? "-" : "") + whole + (decimals ? (decimalComma ? "," : ".") + decimals : "");
  };
  return "Enter " + written(readings.grouped) + " or " + written(readings.decimal) + ", whichever you mean.";
}

/**
 * What a money field holds, for the field that asks: the pence, or the
 * sentence it says when what was typed isn't an amount. Both null for a
 * blank field, which each field answers in its own words ("Enter an amount.",
 * or nought where a blank is nought).
 *
 * @param {unknown} raw
 * @param {{ decimalComma?: boolean }} [options]
 * @returns {{ pence: number|null, error: string|null }}
 */
export function readMoneyInput(raw, options) {
  const decimalComma = Boolean(options && options.decimalComma);
  const read = readMoneyText(raw, { decimalComma });
  if (read.error === "ambiguous") return { pence: null, error: ambiguousAmountSentence(read.readings, { decimalComma }) };
  if (read.error) return { pence: null, error: MONEY_INPUT_COPY[read.error] };
  return { pence: read.pence, error: null };
}

/**
 * What a quantity field holds: the number, or the sentence it says when what
 * was typed isn't a quantity. Both null for a blank field. By the money rule,
 * with no currency and no sign: "1,000" is a thousand, never 1 as parseFloat
 * read it, "1.500" is asked about, and a third decimal place is refused, as a
 * line keeps two (ADR-034). Whether nought or a quantity past a field's
 * ceiling makes sense is the field's own question.
 *
 * @param {unknown} raw
 * @param {{ decimalComma?: boolean }} [options]
 * @returns {{ quantity: number|null, error: string|null }}
 */
export function readQuantityInput(raw, options) {
  const decimalComma = Boolean(options && options.decimalComma);
  const text = String(raw == null ? "" : raw).replace(/\s+/g, " ").trim();
  if (!text) return { quantity: null, error: null };
  if (!/^[\d., ]+$/.test(text)) return { quantity: null, error: QUANTITY_INPUT_COPY.unreadable };
  const read = readMoneyText(text, { decimalComma });
  if (read.error === "ambiguous") return { quantity: null, error: ambiguousAmountSentence(read.readings, { decimalComma, quantity: true }) };
  if (read.error) return { quantity: null, error: QUANTITY_INPUT_COPY[read.error] };
  return { quantity: read.pence / 100, error: null };
}

/**
 * The pounds a money field holds, for the screens that work in pounds: NaN
 * for a blank field and for anything that isn't an amount, which every one
 * of them already refuses. Never a number read out of the front of the text.
 *
 * @param {unknown} raw
 * @returns {number}
 */
export function typedPounds(raw) {
  const read = readMoneyText(raw);
  return read.pence == null ? Number.NaN : read.pence / 100;
}

/**
 * An amount in pounds as a request body carries it: a JSON number, or a
 * string that is a plain decimal ("1200", "1200.50") and nothing else. NaN
 * for anything else, so a route refuses "1,200.00" or "1e3" rather than
 * reading the "1" at the front of it as `parseFloat` did. A body is written
 * by a client, not typed, so it gets no thousands separators or signs.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function bodyPounds(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : Number.NaN;
  if (typeof value !== "string") return Number.NaN;
  const text = value.trim();
  return /^-?(\d+(\.\d*)?|\.\d+)$/.test(text) ? Number(text) : Number.NaN;
}

/**
 * A discount's or a deposit's value as its field holds it: an amount read by
 * the one rule while the field is in pounds (`fixed`), and a percentage as
 * the field has always read one. Null for a blank or unreadable field, with
 * the sentence for an amount that isn't one.
 *
 * @param {"fixed"|"percent"|string} mode
 * @param {unknown} raw
 * @returns {{ value: number|null, error: string|null }}
 */
export function readTermInput(mode, raw) {
  if (mode === "fixed") {
    const read = readMoneyInput(raw);
    return { value: read.pence == null ? null : read.pence / 100, error: read.error };
  }
  const value = Number.parseFloat(String(raw ?? ""));
  return { value: Number.isFinite(value) ? value : null, error: null };
}
